// src/shared/ui/loading/ProgressBar.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface ProgressBarProps {
    children?: React.ReactNode;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ children }) => {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const barRef = useRef<HTMLDivElement>(null);
    const spinnerRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const previousPathRef = useRef(location.pathname);

    // Démarrer la barre et le spinner
    const startProgress = () => {
        setIsLoading(true);
        const bar = barRef.current;
        if (bar) {
            bar.style.width = '0%';
            bar.style.opacity = '1';
            let width = 0;
            const animate = () => {
                if (width >= 80) return;
                width += Math.random() * 15 + 5;
                if (width > 80) width = 80;
                bar.style.width = width + '%';
                timerRef.current = setTimeout(animate, 250);
            };
            animate();
        }

        const spinner = spinnerRef.current;
        if (spinner) {
            spinner.style.opacity = '1';
        }
    };

    // Terminer la barre et cacher le spinner
    const finishProgress = () => {
        setIsLoading(false);
        const bar = barRef.current;
        if (bar) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            bar.style.width = '100%';
            setTimeout(() => {
                bar.style.opacity = '0';
                setTimeout(() => {
                    bar.style.width = '0%';
                }, 200);
            }, 150);
        }

        const spinner = spinnerRef.current;
        if (spinner) {
            setTimeout(() => {
                spinner.style.opacity = '0';
            }, 300);
        }
    };

    // Détecter les changements de route
    useEffect(() => {
        if (previousPathRef.current !== location.pathname) {
            finishProgress();
            previousPathRef.current = location.pathname;
        }
    }, [location.pathname]);

    // Écouter les clics sur les liens
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');
            if (!anchor) return;

            const href = anchor.getAttribute('href');
            const targetAttr = anchor.getAttribute('target');

            if (
                !href ||
                href.startsWith('http') ||
                href.startsWith('//') ||
                href.startsWith('#') ||
                href.startsWith('mailto:') ||
                href.startsWith('tel:') ||
                targetAttr === '_blank' ||
                targetAttr === '_new'
            ) {
                return;
            }

            if (href.startsWith('/')) {
                startProgress();
            }
        };

        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    return (
        <>
            {/* Barre de progression en haut */}
            <div
                ref={barRef}
                className="fixed top-0 left-0 h-1 bg-secondary z-[99999] transition-opacity duration-200"
                style={{ width: '0%', opacity: 0 }}
            />

            {/* ✅ Spinner discret en haut à droite (SANS OVERLAY) */}
            <div
                ref={spinnerRef}
                className="fixed top-20 right-1/2 sm:top-16 sm:right-4 z-[99999] transition-opacity duration-300"
                style={{ opacity: 0 }}
            >
                <div className="relative w-8 h-8 p-1 bg-white rounded-full shadow-lg">
                    {/* Cercle externe avec effet de glow */}
                    <div className="absolute inset-0 rounded-full bg-accent/10 blur-sm animate-pulse" />

                    {/* Spinner principal */}
                    <div className="relative w-full h-full">
                        {/* Fond du spinner */}
                        <div className="absolute inset-0 rounded-full border-2 border-accent/10" />

                        {/* Partie tournante avec dégradé */}
                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-accent border-r-accent/60 animate-spin" />

                        {/* Point central */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                        </div>
                    </div>
                </div>
            </div>

            {children}
        </>
    );
};

export default ProgressBar;